import { CalendarDays } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type DateRange = { startDate: string; endDate: string };
export type RangePreset = '7d' | '30d' | '90d' | 'custom';

const PRESETS: { key: Exclude<RangePreset, 'custom'>; label: string; days: number }[] = [
  { key: '7d', label: '7d', days: 7 },
  { key: '30d', label: '30d', days: 30 },
  { key: '90d', label: '90d', days: 90 },
];

export const computeRange = (
  preset: RangePreset,
  customStart?: string,
  customEnd?: string
): DateRange => {
  if (preset === 'custom' && customStart && customEnd) {
    return { startDate: customStart, endDate: customEnd };
  }

  const days = PRESETS.find(p => p.key === preset)?.days ?? 7;
  const today = new Date();
  const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (days - 1));

  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

type Props = {
  active: RangePreset;
  customStart?: string;
  customEnd?: string;
  onChange: (preset: RangePreset, customStart?: string, customEnd?: string) => void;
};

const DateRangePicker: React.FC<Props> = ({ active, customStart, customEnd, onChange }) => {
  const [open, setOpen] = useState(false);
  const [localStart, setLocalStart] = useState(customStart || '');
  const [localEnd, setLocalEnd] = useState(customEnd || '');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (customStart) setLocalStart(customStart);
    if (customEnd) setLocalEnd(customEnd);
  }, [customStart, customEnd]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node) &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: Math.min(rect.right - 240, window.innerWidth - 260),
      });
    }
  }, [open]);

  const handleApply = () => {
    if (localStart && localEnd && localStart <= localEnd) {
      onChange('custom', localStart, localEnd);
      setOpen(false);
    }
  };

  const customLabel =
    active === 'custom' && customStart && customEnd ? `${customStart} – ${customEnd}` : 'Custom';

  const isValid = localStart && localEnd && localStart <= localEnd;

  return (
    <>
      <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 shadow-sm">
        {PRESETS.map(p => (
          <button
            key={p.key}
            type="button"
            onClick={() => {
              onChange(p.key);
              setOpen(false);
            }}
            className={`px-3 py-1 text-xs font-medium rounded-md transition ${
              active === p.key ? 'bg-primary-500 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {p.label}
          </button>
        ))}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen(v => !v)}
          className={`px-3 py-1 text-xs font-medium rounded-md transition flex items-center gap-1.5 ${
            active === 'custom' ? 'bg-primary-500 text-white' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <CalendarDays className="h-3 w-3" />
          <span className="truncate max-w-[140px]">{customLabel}</span>
        </button>
      </div>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className="w-[240px] rounded-xl border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 p-4"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              zIndex: 9999,
            }}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-3">
              Custom Range
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">From</label>
                <input
                  type="date"
                  value={localStart}
                  onChange={e => setLocalStart(e.target.value)}
                  className="w-full rounded border-gray-300 shadow-sm text-sm px-2 py-1.5 focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">To</label>
                <input
                  type="date"
                  value={localEnd}
                  onChange={e => setLocalEnd(e.target.value)}
                  className="w-full rounded border-gray-300 shadow-sm text-sm px-2 py-1.5 focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              {localStart && localEnd && localStart > localEnd && (
                <p className="text-xs text-red-500">Start date must be before end date</p>
              )}
              <button
                type="button"
                onClick={handleApply}
                disabled={!isValid}
                className="w-full rounded-md bg-primary-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Apply
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default DateRangePicker;
